import { SolutionMap } from "@labs/shared/types/Solution.ts";
import { Target } from "@labs/shared/types/Target.ts";
import { AnalyzedPlan } from "@labs/shared/types/AnalyzedPlan.ts";
import { EmissionsBySource } from "@labs/shared/types/EmissionsBySource.ts";
import { calculateBusinessGrowth } from "./calculateBusinessGrowth.ts";
import { withBusinessGrowth } from "./withBusinessGrowth.ts";
import { cleanerGridAssumption } from "../../data/planAnalysisConstants.ts";
import { generateRecommendations } from "./generateRecommendations.ts";
import { getBuyCleanComponent } from "./getBuyCleanComponent.ts";
import { getOtherComponent } from "./getOtherComponent.ts";
import { getEfficiencyComponentAndResultsByFuel } from "./getEfficiencyComponentAndResultsByFuel.ts";
import { getElectrificationComponents } from "./getElectrificationComponentAndResultsByFuel.ts";
import { getCarbonImpactByScope } from "./getCarbonImpactByScope.ts";
import { getFootprintAfterPlan } from "./getFootprintAfterPlan.ts";
import { getImpactByScopeAndCategory } from "./getImpactByScopeAndCategory.ts";

/**
 * given a solutionMap, return a plan analysis
 * including: the impact on emissions from each of the top level categories
 * * though note that electrification should be broken out into stationary and mobile sources
 * including: the total levelized cost value of the plan (in $-$$$$$ scale)
 * including: the gap between the plan and the target (in terms of % of base year emissions)
 * including: the remaining emissions after the plan is implemented (in terms of % of base year emissions)
 * factor in our projection for a cleaner grid and expected business growth
 */
export const analyzePlan = (
  solutionMap: SolutionMap,
  target: Partial<Target>,
  baselineEmissions: EmissionsBySource,
): AnalyzedPlan => {
  const businessGrowth = calculateBusinessGrowth(target);
  const electricityCarbon = withBusinessGrowth(
    baselineEmissions.electricity,
    businessGrowth,
  );
  const electricityCarbonCleanerGrid =
    electricityCarbon * (1 - cleanerGridAssumption / 100);
  const naturalGasCarbon = withBusinessGrowth(
    baselineEmissions.naturalGas,
    businessGrowth,
  );
  const dieselCarbon = withBusinessGrowth(
    baselineEmissions.diesel,
    businessGrowth,
  );
  const gasolineCarbon = withBusinessGrowth(
    baselineEmissions.gasoline,
    businessGrowth,
  );
  const propaneCarbon = withBusinessGrowth(
    baselineEmissions.propane,
    businessGrowth,
  );
  const fuelOilCarbon = withBusinessGrowth(
    baselineEmissions.fuelOil,
    businessGrowth,
  );
  const refrigerantsCarbon = withBusinessGrowth(
    baselineEmissions.refrigerants,
    businessGrowth,
  );

  const otherCarbon = withBusinessGrowth(
    baselineEmissions.other,
    businessGrowth,
  );

  const cleanerGridImpact =
    (electricityCarbon - electricityCarbonCleanerGrid) * -1;

  const baselineCarbonWithGrowthAndCleanerGrid: EmissionsBySource = {
    electricity: electricityCarbonCleanerGrid,
    naturalGas: naturalGasCarbon,
    diesel: dieselCarbon,
    gasoline: gasolineCarbon,
    propane: propaneCarbon,
    fuelOil: fuelOilCarbon,
    refrigerants: refrigerantsCarbon,
    other: otherCarbon,
  };

  const { efficiencyComponent, efficiencyResultsByFuel } =
    getEfficiencyComponentAndResultsByFuel(
      solutionMap,
      baselineCarbonWithGrowthAndCleanerGrid,
    );

  const {
    stationaryElectrificationComponent,
    mobileElectrificationComponent,
    electrificationResultsByFuel,
  } = getElectrificationComponents(solutionMap, efficiencyResultsByFuel);

  const buyCleanComponent = getBuyCleanComponent(
    solutionMap,
    stationaryElectrificationComponent,
    mobileElectrificationComponent,
    efficiencyResultsByFuel,
    electrificationResultsByFuel,
  );

  const otherComponent = getOtherComponent(
    solutionMap,
    baselineCarbonWithGrowthAndCleanerGrid,
    efficiencyComponent,
    stationaryElectrificationComponent,
    mobileElectrificationComponent,
    buyCleanComponent,
  );

  const allPlanComponents = [
    efficiencyComponent,
    stationaryElectrificationComponent,
    mobileElectrificationComponent,
    buyCleanComponent,
    otherComponent,
  ].filter((component) => component !== null);

  const carbonImpact = allPlanComponents.reduce(
    (acc, component) => acc + (component?.carbonImpact || 0),
    0,
  );

  const businessAsUsual = 100 + businessGrowth * 100 + cleanerGridImpact;
  const asPlanned = businessAsUsual + carbonImpact;
  const goal = typeof target?.goal === "number" ? target.goal : 0;
  const emissionsRemainingAfterGoal = 100 - goal;
  const gap = emissionsRemainingAfterGoal - asPlanned;

  const costScore =
    allPlanComponents.reduce(
      (acc, component) => acc + component.intermediateCostScore,
      0,
    ) / -carbonImpact;

  const carbonImpactByScope = getCarbonImpactByScope(allPlanComponents);

  const impactByScopeAndCategory =
    getImpactByScopeAndCategory(allPlanComponents);

  const footprintAfterPlan = getFootprintAfterPlan(
    baselineCarbonWithGrowthAndCleanerGrid,
    allPlanComponents,
  );

  // re-normalize to baseline year so it can be compared apples-to-apples with target
  const planAbsoluteImpact = asPlanned - 100;

  return {
    businessGrowth: businessGrowth * 100,
    cleanerGrid: cleanerGridImpact,
    baselineCarbonWithGrowthAndCleanerGrid,
    footprintAfterPlan,
    emissionsRemainingAfterPlan: asPlanned,
    costScore,
    carbonImpact,
    gap,
    planAbsoluteImpact,
    impactByScopeAndCategory,
    planComponents: allPlanComponents,
    carbonImpactByScope,
    recommendations: generateRecommendations(
      solutionMap,
      baselineEmissions,
      target,
      gap,
      carbonImpact,
      carbonImpactByScope,
    ),
  };
};
